import { z } from 'zod';

/** Flexible localized map (tr/en or arbitrary locale keys). */
export const localizedMapSchema = z.record(z.string(), z.unknown()).optional();

const hexColor = z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/, 'Invalid hex color')
    .optional();

const fieldTypeEnum = z.enum([
    'text',
    'textarea',
    'number',
    'boolean',
    'date',
    'datetime',
    'select',
    'multiselect',
    'user',
    'unit',
    'role',
    'json',
]);

const validationRuleSchema = z.object({
    kind: z.enum(['regex', 'min', 'max', 'minLength', 'maxLength', 'enum', 'jsonata']),
    value: z.unknown().optional(),
    message: localizedMapSchema,
});

const formLayoutFieldSchema = z.object({
    fieldKey: z.string().min(1),
    source: z.enum(['core', 'custom']).default('core'),
    column: z.number().int().optional(),
    widthSpan: z.number().int().min(1).optional(),
    visibilityExpression: z.string().optional(),
    readOnlyExpression: z.string().optional(),
});

const formLayoutSectionSchema = z.object({
    key: z.string().optional(),
    title: localizedMapSchema,
    description: localizedMapSchema,
    columns: z.number().int().min(1).max(4).optional(),
    visibilityExpression: z.string().optional(),
    collapsible: z.boolean().optional(),
    defaultCollapsed: z.boolean().optional(),
    fields: z.array(formLayoutFieldSchema).default([]),
});

export const customFieldDefinitionCreateSchema = z.object({
    entityType: z.string().min(1),
    fieldKey: z.string().min(1).max(128),
    label: localizedMapSchema,
    description: localizedMapSchema,
    fieldType: fieldTypeEnum,
    options: z
        .array(
            z.object({
                value: z.string(),
                label: localizedMapSchema,
            }),
        )
        .optional(),
    isRequired: z.boolean().optional(),
    isUnique: z.boolean().optional(),
    isSearchable: z.boolean().optional(),
    defaultValue: z.unknown().optional(),
    validations: z.array(validationRuleSchema).optional(),
    visibilityExpression: z.string().optional(),
    requiredExpression: z.string().optional(),
    readOnlyExpression: z.string().optional(),
    placement: z
        .object({
            section: z.string().optional(),
            order: z.number().optional(),
        })
        .optional(),
    isActive: z.boolean().optional(),
    version: z.number().int().optional(),
});

export const customFieldDefinitionUpdateSchema = customFieldDefinitionCreateSchema.partial();

export const entityFormLayoutCreateSchema = z.object({
    entityType: z.string().min(1),
    mode: z.enum(['create', 'update', 'detail']).default('create'),
    sections: z.array(formLayoutSectionSchema).default([]),
    isActive: z.boolean().optional(),
});

export const entityFormLayoutUpdateSchema = entityFormLayoutCreateSchema.partial();

export const customPermissionDefinitionCreateSchema = z.object({
    pageKey: z.string().min(1),
    label: localizedMapSchema,
    actions: z.array(z.string().min(1)).min(1).default(['View']),
    moduleKey: z.string().optional(),
    isActive: z.boolean().optional(),
});

export const customPermissionDefinitionUpdateSchema = customPermissionDefinitionCreateSchema.partial();

export const tenantBrandingUpsertSchema = z.object({
    appName: localizedMapSchema,
    shortName: z.string().optional(),
    logoUrl: z.string().max(4000).optional(),
    faviconUrl: z.string().max(4000).optional(),
    primaryColor: hexColor,
    secondaryColor: hexColor,
    accentColor: hexColor,
    backgroundColor: hexColor,
    textColor: hexColor,
    customCss: z.string().optional(),
    locale: z.string().min(2).optional(),
    timezone: z.string().min(1).optional(),
    supportEmail: z.string().email().optional().or(z.literal('')),
    legalLinks: z
        .array(
            z.object({
                label: localizedMapSchema,
                url: z.string().max(4000),
            }),
        )
        .optional(),
});

export const tenantFeatureFlagUpsertSchema = z.object({
    key: z.string().min(1),
    enabled: z.boolean().optional(),
    rolloutPercent: z.coerce.number().int().min(0).max(100).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
